// The Gantt's date plumbing (package-private): every date a level loader or the date-range
// provider hands the base goes through `parseDate`, and the time axis is picked from the range's
// length by `viewPresetForDays`. VERBATIM from issue-analysis's components/gantts/IssueDueByGantt.vue
// (its `parseDate` and the thresholds of its `getOptimalViewPreset`), IA_ABSORPTION_PLAN Phase 4
// step 13b. No SQL, no table names.

// The preset used when there is no usable date range (no rows, an unparsable date, an error)
export const DEFAULT_VIEW_PRESET = 'hourAndDay';

/**
 * A date as DuckDB/Arrow or an rdrive tag hands it over → `'YYYY-MM-DD'` (or null).
 * Accepts epoch milliseconds (an Arrow timestamp), `'dd/mm/yyyy[ …]'`, `'yyyy-mm-dd[ …]'` and
 * anything `new Date()` parses; a year above 2500 is read as Buddhist Era (minus 543).
 * Idempotent on its own output.
 *
 * @param {number|string|null|undefined} dateString
 * @returns {string|null}
 */
export const parseDate = (dateString) => {
  if (!dateString) return null;

  if (typeof dateString === 'number') {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  }

  if (typeof dateString === 'string') {
    if (dateString.includes('/')) {
      const [datePart] = dateString.split(' ');
      const [day, month, year] = datePart.split('/');
      let numericYear = parseInt(year);
      if (numericYear > 2500) {
        numericYear = numericYear - 543;
      }
      return `${numericYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
      const yearMatch = dateString.match(/^(\d{4})/);
      if (yearMatch) {
        let year = parseInt(yearMatch[1]);
        if (year > 2500) {
          year = year - 543;
          return dateString.replace(/^\d{4}/, year.toString()).split(' ')[0];
        }
      }
      return dateString.split(' ')[0];
    }

    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      if (year > 2500) {
        date.setFullYear(year - 543);
      }
      return date.toISOString().split('T')[0];
    }
  }

  return null;
};

/**
 * The Bryntum view preset for a range of `durationDays` days — hour-level up to a week, then
 * coarser (the old `getOptimalViewPreset`'s thresholds).
 *
 * @param {number} durationDays
 * @returns {string}
 */
export const viewPresetForDays = (durationDays) => {
  if (durationDays <= 7) {
    return 'hourAndDay'; // Hour-level for short durations
  } else if (durationDays <= 14) {
    return 'dayAndWeek'; // For 1-2 weeks
  } else if (durationDays <= 30) {
    return 'weekAndDayLetter'; // For up to a month
  } else if (durationDays <= 90) {
    return 'weekAndMonth'; // For 1-3 months
  } else if (durationDays <= 365) {
    return 'monthAndYear'; // For up to a year
  }
  return 'year'; // For durations over a year
};
